class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.bindOverlayClose();
    this.bindCheckoutGuard();
    this.setHeaderCartIconAccessibility();
    window.addEventListener('resize', () => this.queueScrollableSync());
  }

  bindOverlayClose() {
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (!overlay || overlay.dataset.spclstCloseBound === 'true') return;

    overlay.addEventListener('click', this.close.bind(this));
    overlay.dataset.spclstCloseBound = 'true';
  }

  bindCheckoutGuard() {
    const form = this.querySelector('#CartDrawer-Form');
    if (!form) return;

    const checkoutButton = this.querySelector('#CartDrawer-Checkout');
    if (checkoutButton && checkoutButton.dataset.spclstEmptyCheckoutGuardBound !== 'true') {
      checkoutButton.addEventListener('click', (event) => {
        if (this.shouldBlockEmptyCheckout()) {
          this.blockEmptyCheckout(event, checkoutButton);
        }
      });
      checkoutButton.dataset.spclstEmptyCheckoutGuardBound = 'true';
    }

    if (form.dataset.spclstCheckoutGuardBound === 'true') return;

    form.addEventListener('submit', (event) => {
      const submitter = event.submitter || document.activeElement;
      const isCheckoutSubmit = !submitter || !submitter.name || submitter.name === 'checkout';
      if (!isCheckoutSubmit) return;

      const checkoutButton = this.querySelector('#CartDrawer-Checkout');
      if (this.shouldBlockEmptyCheckout()) {
        this.blockEmptyCheckout(event, checkoutButton);
        return;
      }

      if (form.dataset.spclstCheckoutGuardSubmitting === 'true') {
        delete form.dataset.spclstCheckoutGuardSubmitting;
        return;
      }

      const cartSession = window.SPCLSTCartSession;
      if (!cartSession || typeof cartSession.ensureBeforeCheckout !== 'function') return;

      event.preventDefault();
      if (checkoutButton) checkoutButton.disabled = true;

      cartSession
        .ensureBeforeCheckout({ source: 'cart-drawer-checkout' })
        .then((result = {}) => {
          const itemCount = Number((result.cart && result.cart.item_count) || 0);
          if (itemCount > 0) return true;

          if (window.SPCLSTCartDebug && window.SPCLSTCartDebug.enabled()) {
            window.SPCLSTCartDebug.log('cart drawer checkout stopped for empty cart json', {
              cart: window.SPCLSTCartDebug.summarizeCartJson(result.cart),
              error: result.error || null,
            });
          }

          return this.refreshFromCart().then(() => false);
        })
        .catch((error) => {
          if (window.SPCLSTCartDebug && window.SPCLSTCartDebug.enabled()) {
            window.SPCLSTCartDebug.log('cart drawer checkout guard failed', {
              error: error.message,
            });
          }
          return false;
        })
        .then((shouldSubmit) => {
          if (checkoutButton) checkoutButton.disabled = false;
          if (!shouldSubmit) return;

          form.dataset.spclstCheckoutGuardSubmitting = 'true';

          if (typeof form.requestSubmit === 'function') {
            form.requestSubmit(checkoutButton || undefined);
            return;
          }

          if (checkoutButton) {
            const checkoutInput = document.createElement('input');
            checkoutInput.type = 'hidden';
            checkoutInput.name = checkoutButton.name || 'checkout';
            checkoutInput.value = checkoutButton.value || 'checkout';
            form.appendChild(checkoutInput);
          }
          form.submit();
        });
    });

    form.dataset.spclstCheckoutGuardBound = 'true';
  }

  getVisibleDrawerItemCount() {
    const countElement = this.querySelector('.spclst-cart-drawer-count');
    if (!countElement) return this.hasRenderedCartItems() ? 1 : 0;

    const match = countElement.textContent.match(/\d+/);
    return match ? Number(match[0]) : this.hasRenderedCartItems() ? 1 : 0;
  }

  shouldBlockEmptyCheckout() {
    return !this.hasRenderedCartItems() && this.getVisibleDrawerItemCount() <= 0;
  }

  blockEmptyCheckout(event, checkoutButton) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
    }

    if (checkoutButton) checkoutButton.disabled = true;

    this.syncCartSummary(
      {
        item_count: 0,
        total_price: 0,
        currency:
          window.Shopify && window.Shopify.currency && window.Shopify.currency.active
            ? window.Shopify.currency.active
            : 'USD',
      },
      0
    );
    this.syncEmptyState(0);

    if (window.SPCLSTCartDebug && window.SPCLSTCartDebug.enabled()) {
      window.SPCLSTCartDebug.log('cart drawer empty checkout ignored', {
        hasRenderedCartItems: this.hasRenderedCartItems(),
        visibleDrawerItemCount: this.getVisibleDrawerItemCount(),
      });
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.openWithRefresh(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.openWithRefresh(cartLink);
      }
    });
  }

  open(triggeredBy) {
    this.bindOverlayClose();
    this.bindCheckoutGuard();
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
      this.queueScrollableSync();
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  openWithRefresh(triggeredBy) {
    if (window.SPCLSTCartDebug && window.SPCLSTCartDebug.enabled()) {
      window.SPCLSTCartDebug.log('cart drawer open refresh requested');
    }

    if (this.hasRenderedCartItems()) {
      this.open(triggeredBy);

      if (!this.refreshBeforeOpenPromise) {
        this.refreshBeforeOpenPromise = this.refreshFromCart({ preserveVisibleItemsOnEmpty: true })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            this.refreshBeforeOpenPromise = null;
          });
      }

      return this.refreshBeforeOpenPromise;
    }

    if (!this.refreshBeforeOpenPromise) {
      this.refreshBeforeOpenPromise = this.refreshFromCart()
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          this.refreshBeforeOpenPromise = null;
        });
    }

    return this.refreshBeforeOpenPromise.finally(() => this.open(triggeredBy));
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner) drawerInner.classList.remove('is-empty');
    this.classList.remove('is-empty');
    this.productId = parsedState.id;
    if (typeof parsedState.item_count !== 'undefined') {
      this.syncCartSummary(parsedState, parsedState.item_count);
    }
    const sections = parsedState.sections || {};
    const expectedNonEmptyCart =
      Number(parsedState.item_count || 0) > 0 || !!parsedState.id || !!parsedState.key || !!parsedState.variant_id;
    if (expectedNonEmptyCart) this.syncEmptyState(1);
    const missingSections = [];
    const addMissingSection = (section) => {
      if (!missingSections.some((missingSection) => missingSection.id === section.id)) {
        missingSections.push(section);
      }
    };

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      if (sections[section.id]) {
        const sectionHTML = this.getSectionInnerHTML(sections[section.id], section.selector);
        if (sectionHTML) {
          sectionElement.innerHTML = sectionHTML;
        } else {
          addMissingSection(section);
        }
      } else {
        addMissingSection(section);
      }
    });

    const renderedFallbackLine =
      expectedNonEmptyCart && !this.hasRenderedCartItems() && this.renderFallbackCartItemFromAddResponse(parsedState);
    if (renderedFallbackLine) {
      missingSections.splice(0, missingSections.length);
    }

    const cartDrawerSection = this.getSectionsToRender().find((section) => section.id === 'cart-drawer');
    if (expectedNonEmptyCart && cartDrawerSection && !this.hasRenderedCartItems()) {
      addMissingSection(cartDrawerSection);
    }

    if (window.SPCLSTCartDebug && window.SPCLSTCartDebug.enabled()) {
      window.SPCLSTCartDebug.log('cart drawer render contents', {
        expectedNonEmptyCart,
        hasRenderedCartItems: this.hasRenderedCartItems(),
        missingSections: missingSections.map((section) => section.id),
        sections: window.SPCLSTCartDebug.summarizeSections(sections),
        response: {
          id: parsedState.id || null,
          variant_id: parsedState.variant_id || null,
          item_count: parsedState.item_count,
          hasKey: !!parsedState.key,
        },
        renderedFallbackLine,
      });
    }

    const openDrawer = () => {
      if (expectedNonEmptyCart) this.syncEmptyState(1);
      this.bindOverlayClose();
      this.bindCheckoutGuard();
      this.queueScrollableSync();
      this.open();
    };

    const refreshIfExpectedItemsAreMissing = () => {
      if (!expectedNonEmptyCart || this.hasRenderedCartItems()) return Promise.resolve();
      return this.syncExpectedCartStateFromJson()
        .then((repaired) => {
          if (repaired || this.hasRenderedCartItems()) return;
          return this.refreshFromCart();
        })
        .catch((error) => {
          console.error(error);
          return this.refreshFromCart();
        });
    };

    if (!missingSections.length) {
      refreshIfExpectedItemsAreMissing()
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          setTimeout(openDrawer);
        });
      return;
    }

    Promise.all(missingSections.map((section) => this.renderSectionFromCart(section)))
      .then(refreshIfExpectedItemsAreMissing)
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setTimeout(openDrawer);
      });
  }

  escapeHTML(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, (character) => {
      const entities = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;',
      };

      return entities[character];
    });
  }

  getFallbackVariantLine(line) {
    if (Array.isArray(line.options_with_values) && line.options_with_values.length) {
      return line.options_with_values.map((option) => option.value).filter(Boolean).join(' / ');
    }

    return line.variant_title || '';
  }

  getFallbackLineImage(line) {
    if (line.featured_image && line.featured_image.url) return line.featured_image.url;
    return line.image || '';
  }

  renderFallbackCartItemFromAddResponse(line) {
    if (!line || !(line.variant_id || line.id)) return false;

    const cartItemsContainer = this.querySelector('#CartDrawer-CartItems');
    if (!cartItemsContainer) return false;

    const variantId = line.variant_id || line.id;
    const quantity = Number(line.quantity || 1);
    const title = line.product_title || String(line.title || '').split(' - ')[0] || 'Item';
    const variantLine = this.getFallbackVariantLine(line);
    const url = line.url || (line.handle ? `/products/${line.handle}` : '#');
    const image = this.getFallbackLineImage(line);
    const imageAlt = (line.featured_image && line.featured_image.alt) || title;
    const linePrice = Number(line.line_price || line.final_line_price || line.price || 0);
    const displayTotal = Number(line.line_price || line.final_line_price || (line.price || 0) * quantity || 0);
    const cartState = {
      item_count: quantity,
      total_price: displayTotal,
      currency: window.Shopify && window.Shopify.currency && window.Shopify.currency.active ? window.Shopify.currency.active : 'USD',
    };
    const fallbackHTML = `
      <div class="drawer__cart-items-wrapper">
        <table class="cart-items" role="table">
          <thead role="rowgroup">
            <tr role="row">
              <th id="CartDrawer-ColumnProductImage" role="columnheader"><span class="visually-hidden">Image</span></th>
              <th id="CartDrawer-ColumnProduct" class="caption-with-letter-spacing" scope="col" role="columnheader">Product</th>
              <th id="CartDrawer-ColumnTotal" class="right caption-with-letter-spacing" scope="col" role="columnheader">Total</th>
              <th id="CartDrawer-ColumnQuantity" role="columnheader"><span class="visually-hidden">Quantity</span></th>
            </tr>
          </thead>
          <tbody role="rowgroup">
            <tr id="CartDrawer-Item-1" class="cart-item spclst-cart-item--fallback" role="row">
              <td class="cart-item__media" role="cell" headers="CartDrawer-ColumnProductImage">
                <a href="${this.escapeHTML(url)}" class="cart-item__link" tabindex="-1" aria-hidden="true"> </a>
                ${
                  image
                    ? `<img class="cart-item__image" src="${this.escapeHTML(image)}" alt="${this.escapeHTML(imageAlt)}" loading="lazy" width="150" height="150">`
                    : ''
                }
              </td>
              <td class="cart-item__details" role="cell" headers="CartDrawer-ColumnProduct">
                <a href="${this.escapeHTML(url)}" class="cart-item__name h4 break">${this.escapeHTML(title)}</a>
                ${variantLine ? `<p class="product-option cart-item__variant-line">${this.escapeHTML(variantLine)}</p>` : ''}
              </td>
              <td class="cart-item__totals right" role="cell" headers="CartDrawer-ColumnTotal">
                <div class="loading__spinner hidden"></div>
                <div class="cart-item__price-wrapper">
                  <span class="price price--end">${this.formatCartMoney(displayTotal)}</span>
                </div>
              </td>
              <td class="cart-item__quantity" role="cell" headers="CartDrawer-ColumnQuantity">
                <quantity-popover>
                  <div class="cart-item__quantity-wrapper quantity-popover-wrapper">
                    <div class="quantity-popover-container">
                      <quantity-input class="quantity cart-quantity">
                        <button class="quantity__button" name="minus" type="button">
                          <span class="visually-hidden">Decrease quantity for ${this.escapeHTML(title)}</span>
                          <span class="svg-wrapper"></span>
                        </button>
                        <input
                          class="quantity__input"
                          type="number"
                          data-quantity-variant-id="${this.escapeHTML(variantId)}"
                          name="updates[]"
                          value="${this.escapeHTML(quantity)}"
                          data-cart-quantity="${this.escapeHTML(quantity)}"
                          min="0"
                          data-min="1"
                          step="1"
                          aria-label="Quantity for ${this.escapeHTML(title)}"
                          id="Drawer-quantity-1"
                          data-index="1"
                        >
                        <button class="quantity__button" name="plus" type="button">
                          <span class="visually-hidden">Increase quantity for ${this.escapeHTML(title)}</span>
                          <span class="svg-wrapper"></span>
                        </button>
                      </quantity-input>
                    </div>
                    <cart-remove-button id="CartDrawer-Remove-1" data-index="1">
                      <button
                        type="button"
                        class="button button--tertiary cart-remove-button"
                        aria-label="Remove ${this.escapeHTML(title)}"
                        data-variant-id="${this.escapeHTML(variantId)}"
                      >
                        <span class="cart-remove-text">REMOVE</span>
                      </button>
                    </cart-remove-button>
                  </div>
                  <div id="CartDrawer-LineItemError-1" class="cart-item__error" role="alert">
                    <small class="cart-item__error-text"></small>
                  </div>
                </quantity-popover>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p id="CartDrawer-LiveRegionText" class="visually-hidden" role="status"></p>
      <p id="CartDrawer-LineItemStatus" class="visually-hidden" aria-hidden="true" role="status">Loading...</p>
    `;

    cartItemsContainer.innerHTML = fallbackHTML;
    this.syncCartSummary(cartState, quantity);
    this.syncEmptyState(quantity);
    this.bindCheckoutGuard();
    this.queueScrollableSync();

    if (window.SPCLSTCartDebug && window.SPCLSTCartDebug.enabled()) {
      window.SPCLSTCartDebug.log('cart drawer rendered add-response fallback line', {
        variantId,
        quantity,
        title,
        linePrice,
        displayTotal,
      });
    }

    return true;
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const element = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return element ? element.innerHTML : '';
  }

  getFreshCartSectionUrl(sectionId) {
    const url = new URL(routes.cart_url, window.location.origin);
    url.searchParams.set('section_id', sectionId);
    url.searchParams.set('_', Date.now().toString());
    return url.toString();
  }

  fetchCartSection(sectionId) {
    return fetch(this.getFreshCartSectionUrl(sectionId), {
      cache: 'no-store',
      credentials: 'same-origin',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
    });
  }

  renderSectionFromCart(section) {
    return this.fetchCartSection(section.id)
      .then((response) => {
        if (!response.ok) throw new Error(`Cart section ${section.id} refresh failed with status ${response.status}`);
        return response;
      })
      .then((response) => response.text())
      .then((html) => {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);

        if (!sectionElement) return false;
        const sectionHTML = this.getSectionInnerHTML(html, section.selector);
        if (!sectionHTML) return false;

        sectionElement.innerHTML = sectionHTML;
        this.bindCheckoutGuard();
        return true;
      });
  }

  getFreshCartJsonUrl() {
    const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    const localeRoot = root ? (root.endsWith('/') ? root : `${root}/`) : '/';
    const url = new URL(`${localeRoot}cart.js`, window.location.origin);
    url.searchParams.set('_', Date.now().toString());
    return url.toString();
  }

  fetchCartJson() {
    return fetch(this.getFreshCartJsonUrl(), {
      cache: 'no-store',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
    }).then((response) => {
      if (!response.ok) throw new Error(`Cart JSON refresh failed with status ${response.status}`);
      return response.json();
    });
  }

  hasRenderedCartItems() {
    return !!this.querySelector('[id^="CartDrawer-Item-"]');
  }

  formatCartMoney(cents, currency = 'USD') {
    const amount = Number(cents || 0);
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(amount);
    }

    return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
      style: 'currency',
      currency,
    }).format(amount / 100);
  }

  syncExpectedCartStateFromJson() {
    if (this.hasRenderedCartItems()) return Promise.resolve(true);

    return this.fetchCartJson().then((cartState) => {
      const itemCount = Number((cartState && cartState.item_count) || 0);
      if (window.SPCLSTCartDebug && window.SPCLSTCartDebug.enabled()) {
        window.SPCLSTCartDebug.log('cart drawer cart json sync', {
          itemCount,
          hasRenderedCartItems: this.hasRenderedCartItems(),
          cart: window.SPCLSTCartDebug.summarizeCartJson(cartState),
        });
      }

      if (itemCount <= 0) {
        this.syncCartSummary(cartState, itemCount);
        this.syncEmptyState(itemCount);
        return false;
      }

      this.syncCartSummary(cartState, itemCount);
      this.syncEmptyState(itemCount);

      console.warn('[SPCLST cart] Drawer section was empty while cart had items; retrying Shopify cart sections.');
      return false;
    });
  }

  syncCartSummary(cartState, itemCount) {
    const count = Number(itemCount || 0);
    const countElement = this.querySelector('.spclst-cart-drawer-count');
    const totalElement = this.querySelector('[data-spclst-cart-total]');
    const checkoutButton = this.querySelector('#CartDrawer-Checkout');

    if (countElement) countElement.textContent = `${count} ITEMS`;
    this.syncHeaderCartCount(count);
    if (cartState && totalElement) totalElement.textContent = this.formatCartMoney(cartState.total_price, cartState.currency);
    if (checkoutButton) checkoutButton.disabled = count === 0;
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cartState || null } }));
  }

  syncHeaderCartCount(itemCount) {
    const count = Number(itemCount || 0);
    const visibleCount = count < 10 ? `0${count}` : String(count);
    const accessibleCount = count === 1 ? '1 item' : `${count} items`;

    document.querySelectorAll('#cart-icon-bubble, [data-spclst-open-cart-drawer]').forEach((cartLink) => {
      const hiddenCountElement = cartLink.querySelector('.visually-hidden');

      cartLink.querySelectorAll('.cartcount, .mobilecartcount').forEach((countElement) => {
        countElement.textContent = visibleCount;
      });
      if (hiddenCountElement) hiddenCountElement.textContent = accessibleCount;
      cartLink.setAttribute('aria-label', `Cart, ${accessibleCount}`);
    });
  }

  syncEmptyState(itemCount) {
    const isEmpty = Number(itemCount || 0) === 0;
    const drawerInner = this.querySelector('.drawer__inner');
    const drawerItems = this.querySelector('cart-drawer-items');
    const drawerFooter = this.querySelector('.drawer__footer');

    this.classList.toggle('is-empty', isEmpty);
    if (drawerInner) drawerInner.classList.toggle('is-empty', isEmpty);
    if (drawerItems) drawerItems.classList.toggle('is-empty', isEmpty);
    if (drawerFooter) drawerFooter.classList.toggle('is-empty', isEmpty);
  }

  refreshFromCart(options = {}) {
    const preserveVisibleItemsOnEmpty = !!options.preserveVisibleItemsOnEmpty;
    const hadRenderedCartItems = this.hasRenderedCartItems();

    return this.fetchCartJson().catch((error) => {
      console.error(error);
      return null;
    }).then((cartState) => {
      const itemCount =
        cartState && typeof cartState.item_count !== 'undefined'
          ? Number(cartState.item_count || 0)
          : this.hasRenderedCartItems()
            ? 1
            : 0;
      const cartDrawerSection = this.getSectionsToRender().find((section) => section.id === 'cart-drawer');

      if (preserveVisibleItemsOnEmpty && hadRenderedCartItems && itemCount <= 0) {
        if (window.SPCLSTCartDebug && window.SPCLSTCartDebug.enabled()) {
          window.SPCLSTCartDebug.log('cart drawer preserved visible items after empty refresh', {
            cart: window.SPCLSTCartDebug.summarizeCartJson(cartState),
          });
        }

        this.bindOverlayClose();
        this.bindCheckoutGuard();
        this.queueScrollableSync();
        return;
      }

      const sectionRequests = this.getSectionsToRender().map((section) =>
        this.renderSectionFromCart(section).catch((error) => {
          console.error(error);
        })
      );
      const finishRefresh = () => {
        if (window.SPCLSTCartDebug && window.SPCLSTCartDebug.enabled()) {
          window.SPCLSTCartDebug.log('cart drawer refresh from cart', {
            itemCount,
            hasRenderedCartItems: this.hasRenderedCartItems(),
            cart: window.SPCLSTCartDebug.summarizeCartJson(cartState),
          });
        }

        this.syncCartSummary(cartState, itemCount);
        this.syncEmptyState(itemCount);
        this.bindOverlayClose();
        this.bindCheckoutGuard();
        this.queueScrollableSync();
      };

      this.syncEmptyState(itemCount);

      if (itemCount > 0 && cartDrawerSection && !this.hasRenderedCartItems()) {
        return this.renderSectionFromCart(cartDrawerSection)
          .catch((error) => {
            console.error(error);
          })
          .then(finishRefresh);
      }

      finishRefresh();
    });
  }

  queueScrollableSync() {
    window.requestAnimationFrame(() => {
      this.syncScrollableItems();
      window.setTimeout(() => this.syncScrollableItems(), 120);
    });
  }

  syncScrollableItems() {
    const itemsWrapper = this.querySelector('.drawer__cart-items-wrapper');
    if (!itemsWrapper) return;

    const hasOverflow = itemsWrapper.scrollHeight > itemsWrapper.clientHeight + 2;
    itemsWrapper.classList.toggle('spclst-cart-items-can-scroll', hasOverflow);
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
